import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  isFounder,
  hasRegistered,
  markInductionWatched,
  hasWatchedInduction,
} from "@/utils/founderAccess";

// Council Induction / Grand Opening cinematic — 12s close-up,
// position-locked render. Amanda (Shimmer TTS) stays seated at
// the head of the table throughout — no teleportation between
// establishing shot and close-up. Table edge visible at waist,
// background figures blurred + motionless. Auto-plays muted at
// the top of /roundtable; visitor taps to unmute and hear the
// induction. Wide establishing shot alt exists at
// .../videos/c00ac8f14957ed37.mp4 (6s) if we ever want to prefix
// the close-up with a wide.
const COUNCIL_INDUCTION_VIDEO =
  "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/0fb4d2da6f20cf01.mp4";

// ---------------------------------------------------------------
// /roundtable — Executive Summit · Cinematic reskin
//
// Design brief: TED-stage / Nat-Geo documentary title card.
// - Rich black background, warm-gold accents
// - Playfair Display serif for headings, Inter for body
// - Fade-in title sequence, subtle gold divider rules
// - Speaker Gallery with play-button thumbnails → lightbox modal
// - Direct Briefing textarea + gold submit → styled answer card
//
// Backend contract unchanged: POST /api/roundtable/ask with
// {question, matched_member, company, contact}.
// ---------------------------------------------------------------

const GOLD = "#C9A84C";
const GOLD_SOFT = "rgba(201,168,76,0.55)";
const INK = "#0A0A0A";
const CARD = "#1A1A1A";
const CARD_HI = "#1E1E1E";
const HAIRLINE = "rgba(201,168,76,0.22)";
const MUTED = "#B7B7B0";
const API = process.env.REACT_APP_BACKEND_URL;

const SERIF = `'Playfair Display', 'Cormorant Garamond', Georgia, serif`;
const SANS = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`;

// Council roster. Ordering here is the on-screen render order.
// Keywords power the client-side router (Ask The Council).
//
// -----------------------------------------------------------
// DRESS CODE AUDIT · reference for future renders
// -----------------------------------------------------------
// Each member's video must visually match the wardrobe +
// background locked below. If a new render is dropped in,
// re-check against this list before shipping.
//   ALFRED   · charcoal crew-neck OR navy blazer over white
//              shirt · dim command-center / cockpit desk BG
//   AMANDA   · black tailored pantsuit, white silk blouse,
//              long wavy brown hair · bright modern corporate
//              foyer BG
//   STERLING · sharp black suit, silver tie, closely groomed
//              · minimalist glass-and-steel industrial office BG
//   VANCE    · weathered denim shirt / high-end work shirt,
//              sleeves slightly rolled · cinematic workshop
//              w/ tools OR outdoor Field Ops BG
//   AMBER    · light grey OR sage blazer, clean lines · bright
//              sunlit observation deck OR quiet workspace BG
//   SLOAN    · premium polo with subtle Grid emblem · high-tech
//              research center OR lush organized commercial
//              greenhouse BG
// Visual match must be verified manually — the video URLs alone
// cannot be inspected in code. Flag any mismatch here with a
// per-member `// DRESS-CODE MISMATCH: ...` note.
// -----------------------------------------------------------
const COUNCIL = [
  {
    key: "ALFRED",
    title: "Chief Visionary Officer",
    strengths: "Visionary Strategy & Grid Implementation",
    keywords: [
      "strategy", "vision", "architecture", "positioning", "competitive",
      "market", "plan", "last 50 miles", "dominance", "expansion", "grow",
    ],
    video:
      "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/a41b3a5a3a8c03ad.mp4",
  },
  {
    key: "AMANDA",
    title: "VP of Lead Generation",
    strengths: "Lead Capture & Regional Authority",
    keywords: [
      "leads", "lead", "onboarding", "induction", "getting started",
      "new client", "capture", "intake", "join", "first step",
      "regional", "authority",
    ],
    video:
      "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/90c3cbcaf205800e.mp4",
  },
  {
    key: "STERLING",
    title: "Director of Operations",
    strengths: "Operations Velocity & Scheduling Logistics",
    keywords: [
      "operations", "crew", "dispatch", "schedule", "scheduling", "billing",
      "invoicing", "invoice", "staff", "route", "field", "logistics",
    ],
    video:
      "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/da76ac770020d631.mp4",
  },
  {
    key: "VANCE",
    title: "VP of Market Strategy",
    strengths: "Market Resonance & Strategic Storytelling",
    keywords: [
      "marketing", "story", "storytelling", "social", "content", "ads",
      "reviews", "google", "facebook", "tiktok", "instagram", "seo",
      "brand", "resonance",
    ],
    video:
      "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/70e95b9f4dc9be99.mp4",
  },
  {
    key: "AMBER",
    title: "Quality Assurance Lead",
    strengths: "Quality Audit & Friction Reduction",
    keywords: [
      "quality", "audit", "compliance", "standards", "friction", "error",
      "problem", "issue", "check", "review", "complaint",
    ],
    video:
      "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/fe6360e686daecba.mp4",
  },
  {
    key: "SLOAN",
    title: "Technical Director",
    strengths: "Technical Education & Plant Intelligence",
    keywords: [
      // Core lawn / turf / equipment terms
      "plant", "equipment", "lawn", "grass", "fertilizer", "fertilize",
      "mowing", "mow", "irrigation", "weed", "seed", "technique", "how to",
      "tip", "aeration", "soil", "fescue", "bermuda",
      // Extended horticultural + plant-intelligence vocabulary
      "tree", "native", "bonsai", "shrub", "prune", "pruning",
      "mulch", "compost", "drainage", "shade", "sun",
      "perennial", "annual", "flower", "bloom",
      "root", "leaf", "leaves", "branch", "bark",
      "pest", "fungus", "disease", "organic", "topsoil",
      "ph", "nitrogen", "phosphorus", "potassium",
      "zone", "hardiness", "drought", "xeriscaping",
      "landscape", "landscaping", "garden", "gardening",
      "planting", "transplant", "propagation", "cutting", "graft",
      "canopy", "understory", "invasive",
      "herbicide", "pesticide", "insect", "grub",
      "thatch", "overseed", "dethatching",
      "edging", "trimming", "hedge", "groundcover",
      "sod", "turf",
      "identify", "identification", "species", "variety", "cultivar",
    ],
    video:
      "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/d63afe663a11911f.mp4",
  },
];

// Whichever member has the most keyword hits in the (lowercased)
// question wins. Ties resolve by roster order. Zero hits → ALFRED
// (CVO-first fallback in the Summit reskin).
function matchCouncilMember(question) {
  const q = (question || "").toLowerCase();
  if (!q.trim()) return "ALFRED";
  let best = { key: "ALFRED", score: 0 };
  for (const m of COUNCIL) {
    let score = 0;
    for (const kw of m.keywords) if (q.includes(kw)) score += 1;
    if (score > best.score) best = { key: m.key, score };
  }
  return best.score > 0 ? best.key : "ALFRED";
}

export default function Roundtable() {
  const [params] = useSearchParams();
  const contact = (params.get("contact") || "Friend").trim();
  const company = (params.get("company") || "Your Business").trim();
  const city = (params.get("city") || "Your City").trim();
  const initialQuery = (params.get("q") || "").trim();
  const requestedExpert = (params.get("expert") || "").trim().toUpperCase();
  // Direct-link (Surgical Strike ad) mode: both `q` and `expert`
  // are present → skip Grand Opening video, skip register gate,
  // and auto-fire the answer flow on mount.
  const isDirectLink = !!(initialQuery && requestedExpert);
  const founder = isFounder();
  // Soft gate — anyone landing on /roundtable without a prior
  // registration (via /register) and without founder access
  // gets redirected to /register. Founders + Surgical-Strike
  // direct links bypass automatically.
  const gateBlocked = !isDirectLink && !founder && !hasRegistered();

  const [question, setQuestion] = useState(initialQuery);
  const [matched, setMatched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [answer, setAnswer] = useState(null);
  const [openMember, setOpenMember] = useState(null); // lightbox
  const [showSandbox, setShowSandbox] = useState(false);
  const answerRef = useRef(null);
  const autoAskedRef = useRef(false);

  const activeMember = useMemo(
    () => COUNCIL.find((m) => m.key === matched) || null,
    [matched],
  );

  // Lock body scroll while the video lightbox or sandbox is open
  // and close on Esc.
  useEffect(() => {
    if (!openMember && !showSandbox) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenMember(null);
        setShowSandbox(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [openMember, showSandbox]);

  // Central ask flow — used by the manual form submit AND by the
  // auto-fire path for `?q=&expert=` direct links.
  const doAsk = async (q, forcedMember) => {
    if (!q || loading) return;
    // `expert` param wins when it names a valid Council member,
    // otherwise fall back to the keyword matcher.
    const validForced =
      forcedMember && COUNCIL.some((m) => m.key === forcedMember)
        ? forcedMember
        : null;
    const member = validForced || matchCouncilMember(q);
    setMatched(member);
    setAnswer(null);
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/roundtable/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: q,
          matched_member: member,
          company,
          contact,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || `Council unreachable (HTTP ${res.status})`);
      }
      setAnswer({
        member: data.member || member,
        title: data.title || "",
        response: data.response || "",
        timestamp: data.timestamp || new Date().toISOString(),
      });
      setTimeout(() => {
        if (answerRef.current)
          answerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
      }, 60);
    } catch (err) {
      console.error("[Roundtable] ask failed:", err);
      setError((err && err.message) || "Council unreachable. Try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  const ask = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    doAsk(question.trim());
  };

  // Auto-fire once for Surgical Strike direct links.
  useEffect(() => {
    if (!isDirectLink || autoAskedRef.current || gateBlocked) return;
    autoAskedRef.current = true;
    doAsk(initialQuery, requestedExpert);
    // Direct-link visitors have effectively "watched" the induction —
    // mark it so a manual reload doesn't relaunch the Grand Opening.
    try { markInductionWatched(); } catch (_) { /* ignore */ }
  }, []);

  return (
    <div
      data-testid="roundtable-root"
      style={{
        minHeight: "100vh",
        background: INK,
        color: "#EDEBE4",
        fontFamily: SANS,
      }}
    >
      {gateBlocked && <Navigate to="/register" replace />}

      {/* ============ COUNCIL INDUCTION · GRAND OPENING ============
          22-second cinematic that plays before the title sequence.
          Auto-plays muted (browser autoplay policy); the visitor
          taps the gold overlay to unmute and hear Amanda deliver
          the induction. Once dismissed or played through, the
          hero collapses to a compact "REPLAY GRAND OPENING" pill.
          Surgical Strike direct links (`?q=&expert=`) skip it
          entirely so the ad campaign lands straight on the answer. */}
      {!isDirectLink && <CouncilInduction paused={!!openMember} />}
      {/* Scoped stylesheet — animations, hover states, focus rings.
          Kept inside the component so nothing leaks to other routes. */}
      <style>{`
        @keyframes rt-fade-up {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes rt-fade-in {
          from { opacity: 0; } to { opacity: 1; }
        }
        @keyframes rt-glow {
          0%,100% { box-shadow: 0 0 0 0 rgba(201,168,76,0.28), 0 24px 60px -30px rgba(201,168,76,0.35); }
          50%     { box-shadow: 0 0 0 6px rgba(201,168,76,0.10), 0 30px 70px -25px rgba(201,168,76,0.55); }
        }
        @keyframes rt-dot {
          0%,80%,100% { opacity:.25; transform:translateY(0); }
          40% { opacity:1; transform:translateY(-3px); }
        }
        .rt-fade  { animation: rt-fade-up 900ms ease-out both; }
        .rt-fade2 { animation: rt-fade-up 900ms ease-out both; animation-delay: 180ms; }
        .rt-fade3 { animation: rt-fade-up 900ms ease-out both; animation-delay: 360ms; }
        .rt-in    { animation: rt-fade-in  1200ms ease both; }

        .rt-speaker { transition: transform 280ms ease, border-color 280ms ease, box-shadow 280ms ease; }
        .rt-speaker:hover {
          transform: translateY(-4px) scale(1.02);
          border-color: ${GOLD};
          box-shadow: 0 30px 60px -25px rgba(201,168,76,0.45), inset 0 0 0 1px rgba(201,168,76,0.35);
        }
        .rt-active { animation: rt-glow 1.8s ease-in-out infinite; border-color: ${GOLD} !important; }

        .rt-play {
          transition: transform 260ms ease, background 260ms ease;
        }
        .rt-thumb:hover .rt-play {
          transform: translate(-50%, -50%) scale(1.08);
          background: ${GOLD};
          color: #000;
        }

        .rt-dot { display:inline-block; width:6px; height:6px; border-radius:50%; background:${GOLD}; margin:0 3px; animation: rt-dot 1.1s ease-in-out infinite; }
        .rt-dot:nth-child(2){ animation-delay:.15s; }
        .rt-dot:nth-child(3){ animation-delay:.3s; }

        .rt-textarea:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.20); }
        .rt-submit:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 20px 40px -14px ${GOLD}; }
        .rt-submit { transition: transform 220ms ease, box-shadow 220ms ease, opacity 220ms ease; }

        .rt-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, ${GOLD} 20%, ${GOLD} 80%, transparent);
          opacity: 0.55;
        }

        @media (max-width: 720px) {
          .rt-title { font-size: 2.4rem !important; letter-spacing: 0 !important; }
          .rt-hero  { padding-top: 60px !important; padding-bottom: 60px !important; }
        }
      `}</style>

      {/* ============ TITLE SEQUENCE ============ */}
      <section
        className="rt-hero"
        style={{
          padding: "112px 24px 88px",
          borderBottom: `1px solid ${HAIRLINE}`,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* faint radial vignette so title reads like a stage light */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(60% 60% at 50% 20%, rgba(201,168,76,0.10), rgba(0,0,0,0) 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          className="rt-fade"
          style={{
            display: "inline-block",
            padding: "8px 18px",
            border: `1px solid ${GOLD_SOFT}`,
            color: GOLD,
            fontFamily: SANS,
            fontSize: 11,
            letterSpacing: "0.42em",
            textTransform: "uppercase",
            fontWeight: 600,
            marginBottom: 28,
            borderRadius: 999,
          }}
          data-testid="roundtable-kicker"
        >
          Executive Summit · MMXXVI
        </div>
        <h1
          className="rt-title rt-fade2"
          data-testid="roundtable-title"
          style={{
            fontFamily: SERIF,
            fontWeight: 700,
            fontSize: "clamp(2.6rem, 6vw, 4.6rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
            margin: 0,
            color: "#F5F1E3",
          }}
        >
          WA Landscapers Growth Roundtable
        </h1>
        <div
          className="rt-fade3"
          data-testid="roundtable-subtitle"
          style={{
            marginTop: 20,
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
            color: GOLD,
            letterSpacing: "0.14em",
          }}
        >
          Executive Summit &nbsp;•&nbsp; Strategic Council Briefing
        </div>
        <div
          className="rt-fade3"
          data-testid="roundtable-representing"
          style={{
            marginTop: 32,
            color: MUTED,
            fontFamily: SANS,
            fontSize: "0.95rem",
            letterSpacing: "0.06em",
          }}
        >
          Convened for&nbsp;
          <span style={{ color: "#F5F1E3", fontWeight: 600 }}>{contact}</span>
          &nbsp;on behalf of&nbsp;
          <span style={{ color: GOLD, fontWeight: 700 }}>{company}</span>
        </div>

        {founder && (
          <div
            className="rt-fade3"
            data-testid="roundtable-founder-badge"
            style={{
              margin: "36px auto 0",
              maxWidth: 560,
              background: "linear-gradient(160deg, rgba(201,168,76,0.16) 0%, rgba(10,10,10,0.6) 90%)",
              border: `1px solid ${GOLD}`,
              borderRadius: 12,
              padding: "14px 20px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              boxShadow: `0 24px 60px -30px rgba(201,168,76,0.55)`,
              textAlign: "left",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: GOLD,
                color: "#0A0A0A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                fontWeight: 900,
                flexShrink: 0,
              }}
            >
              ★
            </span>
            <div>
              <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.36em", textTransform: "uppercase", fontWeight: 700 }}>
                Founder Access Granted
              </div>
              <div style={{ color: "#F5F1E3", fontSize: "0.95rem", marginTop: 2 }}>
                Intelligence Tier unlocked · Sloan protocols included
              </div>
            </div>
          </div>
        )}

        <div
          className="rt-fade3"
          style={{
            margin: "56px auto 0",
            width: 220,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          }}
        />
      </section>

      {/* ============ SPEAKER GALLERY ============ */}
      <section
        style={{
          padding: "72px 24px 40px",
          maxWidth: 1240,
          margin: "0 auto",
        }}
      >
        <SectionHeading
          eyebrow="Speaker Gallery"
          title="The Council"
          subtitle="Six voices. Six departments. Every question routes to the lane that owns it."
        />

        <div
          data-testid="roundtable-grid"
          style={{
            marginTop: 44,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 22,
          }}
        >
          {COUNCIL.map((m) => {
            const active = matched === m.key;
            return (
              <article
                key={m.key}
                data-testid={`roundtable-card-${m.key.toLowerCase()}`}
                className={`rt-speaker ${active ? "rt-active" : ""}`}
                style={{
                  background: CARD,
                  border: `1px solid ${active ? GOLD : HAIRLINE}`,
                  borderRadius: 14,
                  padding: 18,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  position: "relative",
                }}
              >
                {/* Video thumbnail with centered play button. Clicking
                    anywhere in the thumb opens the lightbox modal. */}
                <button
                  type="button"
                  onClick={() => setOpenMember(m.key)}
                  aria-label={`Play ${m.key} briefing`}
                  data-testid={`roundtable-open-${m.key.toLowerCase()}`}
                  className="rt-thumb"
                  style={{
                    position: "relative",
                    background: "#000",
                    border: `1px solid ${HAIRLINE}`,
                    borderRadius: 10,
                    aspectRatio: "16 / 9",
                    width: "100%",
                    padding: 0,
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  {/* Native <video> as its own poster — first frame paints. */}
                  <video
                    src={m.video}
                    muted
                    playsInline
                    preload="metadata"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      opacity: 0.85,
                    }}
                  />
                  {/* Vignette so the play button reads clean over any frame */}
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)",
                    }}
                  />
                  <div
                    className="rt-play"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: 68,
                      height: 68,
                      borderRadius: "50%",
                      background: "rgba(201,168,76,0.92)",
                      color: "#000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 12px 30px -6px rgba(201,168,76,0.55)`,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 0,
                        height: 0,
                        borderLeft: "16px solid #0A0A0A",
                        borderTop: "10px solid transparent",
                        borderBottom: "10px solid transparent",
                        marginLeft: 5,
                      }}
                    />
                  </div>
                </button>

                <div>
                  <div
                    style={{
                      fontFamily: SERIF,
                      color: GOLD,
                      fontSize: "1.9rem",
                      fontWeight: 700,
                      letterSpacing: "0.02em",
                      lineHeight: 1,
                    }}
                  >
                    {m.key.charAt(0) + m.key.slice(1).toLowerCase()}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      color: "#E8E4D6",
                      fontFamily: SANS,
                      fontSize: "0.98rem",
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {m.title}
                  </div>
                  <div
                    style={{
                      marginTop: 10,
                      color: GOLD,
                      fontFamily: SERIF,
                      fontStyle: "italic",
                      fontSize: "0.95rem",
                      letterSpacing: "0.02em",
                      lineHeight: 1.45,
                    }}
                  >
                    {m.strengths}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ============ DIVIDER ============ */}
      <div className="rt-divider" style={{ margin: "40px auto", maxWidth: 1240 }} />

      {/* ============ DIRECT BRIEFING ============ */}
      <section
        data-testid="roundtable-ask-section"
        style={{
          padding: "40px 24px 40px",
          maxWidth: 940,
          margin: "0 auto",
        }}
      >
        <SectionHeading
          eyebrow="Direct Briefing"
          title="Submit your question to the council"
          subtitle="Type a strategic question. It routes to the council member whose department owns the answer."
        />

        <form
          onSubmit={ask}
          style={{ marginTop: 32, display: "grid", gap: 14 }}
        >
          <textarea
            className="rt-textarea"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What strategic question do you need answered?"
            disabled={loading}
            data-testid="roundtable-input"
            rows={4}
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: CARD,
              color: "#F5F1E3",
              border: `1px solid ${GOLD_SOFT}`,
              borderRadius: 10,
              padding: "16px 18px",
              fontSize: "1.02rem",
              lineHeight: 1.55,
              fontFamily: SANS,
              outline: "none",
              resize: "vertical",
              minHeight: 130,
              transition: "border-color 220ms ease, box-shadow 220ms ease",
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              className="rt-submit"
              data-testid="roundtable-submit"
              disabled={loading || !question.trim()}
              style={{
                background: GOLD,
                color: "#0A0A0A",
                padding: "15px 28px",
                fontSize: "0.92rem",
                border: "none",
                borderRadius: 8,
                cursor: loading || !question.trim() ? "not-allowed" : "pointer",
                fontFamily: SANS,
                fontWeight: 700,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                opacity: loading || !question.trim() ? 0.55 : 1,
                boxShadow: `0 14px 30px -12px ${GOLD}`,
              }}
            >
              {loading ? "Reviewing…" : "Submit Briefing"}
            </button>
          </div>
        </form>

        {loading && activeMember && (
          <div
            data-testid="roundtable-typing"
            style={{
              marginTop: 22,
              color: "#E8E4D6",
              fontSize: "1rem",
              fontFamily: SANS,
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "center",
            }}
          >
            <span
              style={{
                color: GOLD,
                fontFamily: SERIF,
                fontSize: "1.15rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
              }}
            >
              {activeMember.key.charAt(0) + activeMember.key.slice(1).toLowerCase()}
            </span>
            <span style={{ color: MUTED }}>is reviewing your question</span>
            <span>
              <span className="rt-dot" />
              <span className="rt-dot" />
              <span className="rt-dot" />
            </span>
          </div>
        )}

        {error && !loading && (
          <div
            data-testid="roundtable-error"
            style={{
              marginTop: 22,
              background: "linear-gradient(160deg, rgba(239,68,68,0.10), rgba(10,10,10,0.9))",
              border: "1px solid rgba(239,68,68,0.55)",
              borderRadius: 10,
              padding: "16px 18px",
              color: "#fff",
              fontSize: "0.98rem",
              fontFamily: SANS,
            }}
          >
            {error}
          </div>
        )}

        {answer && !loading && (
          <div
            ref={answerRef}
            data-testid="roundtable-answer"
            className="rt-in"
            style={{
              marginTop: 26,
              background: CARD_HI,
              borderLeft: `4px solid ${GOLD}`,
              borderTop: `1px solid ${HAIRLINE}`,
              borderRight: `1px solid ${HAIRLINE}`,
              borderBottom: `1px solid ${HAIRLINE}`,
              borderRadius: 8,
              padding: "26px 28px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 14,
              }}
            >
              <span
                style={{
                  fontFamily: SERIF,
                  color: GOLD,
                  fontSize: "1.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  lineHeight: 1,
                }}
              >
                {answer.member.charAt(0) + answer.member.slice(1).toLowerCase()}
              </span>
              <span
                style={{
                  color: "#E8E4D6",
                  fontFamily: SANS,
                  fontSize: "0.95rem",
                  letterSpacing: "0.04em",
                }}
              >
                · {answer.title}
              </span>
            </div>
            <div
              data-testid="roundtable-answer-body"
              style={{
                color: "#F0EDE0",
                fontSize: "1.02rem",
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                fontFamily: SANS,
              }}
            >
              {answer.response}
            </div>
            <div
              style={{
                marginTop: 18,
                color: MUTED,
                fontSize: 11,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                fontWeight: 700,
                fontFamily: SANS,
              }}
            >
              Logged · {new Date(answer.timestamp).toLocaleString()}
            </div>

            {/* Sandbox reveal — the answer is the moment of highest
                intent. Fade in a gold CTA that opens the mock
                Command Center backoffice so the prospect can feel
                the Grid before they lock in a tier. */}
            <SeeYourShopButton onClick={() => setShowSandbox(true)} />
          </div>
        )}
      </section>

      {/* ============ SURGICAL STRIKE SCRIPTS ============
          Public-facing library of the 4 ad scripts. Each has a
          one-tap copy for the deep-link URL so ops can slap them
          onto TikTok / Reels / YT Shorts in seconds. */}
      <SurgicalScripts />

      {/* ============ SANDBOX BACKOFFICE MODAL ============ */}
      {showSandbox && (
        <SandboxBackoffice
          company={company}
          city={city}
          onClose={() => setShowSandbox(false)}
        />
      )}

      {/* ============ COMMAND CENTER CTA ============ */}
      <section
        data-testid="roundtable-cta-section"
        style={{
          padding: "72px 24px 24px",
          textAlign: "center",
          maxWidth: 720,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            color: GOLD,
            fontFamily: SANS,
            fontSize: 11,
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          Ready to Activate
        </div>
        <h2
          style={{
            fontFamily: SERIF,
            color: "#F5F1E3",
            fontSize: "clamp(1.8rem, 3.4vw, 2.4rem)",
            fontWeight: 700,
            margin: "12px 0 10px",
            letterSpacing: "-0.005em",
          }}
        >
          The Grid is prepared for you.
        </h2>
        <p style={{ color: MUTED, fontFamily: SANS, fontSize: "1rem", lineHeight: 1.55, margin: "0 0 28px" }}>
          Choose your node tier and turn today&apos;s briefing into a running Command Center.
        </p>
        <Link
          to="/activate-node"
          data-testid="roundtable-cta-lock-in"
          style={{
            display: "inline-block",
            background: GOLD,
            color: "#0A0A0A",
            padding: "18px 36px",
            fontFamily: SANS,
            fontSize: "0.92rem",
            border: "none",
            borderRadius: 10,
            textDecoration: "none",
            fontWeight: 800,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            boxShadow: `0 20px 44px -14px ${GOLD}`,
          }}
        >
          Lock In Your Command Center
        </Link>
      </section>

      {/* ============ FOOTER ============ */}
      <footer
        style={{
          marginTop: 60,
          padding: "28px 24px 40px",
          borderTop: `1px solid ${HAIRLINE}`,
          textAlign: "center",
        }}
      >
        <div
          style={{
            color: GOLD,
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: "0.9rem",
            letterSpacing: "0.06em",
          }}
        >
          © Wingman Alfred &nbsp;•&nbsp; Executive Council
        </div>
      </footer>

      {/* ============ LIGHTBOX MODAL ============ */}
      {openMember && (
        <VideoLightbox
          member={COUNCIL.find((m) => m.key === openMember)}
          onClose={() => setOpenMember(null)}
        />
      )}
    </div>
  );
}

// ---- Section heading atom ----------------------------------
function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          color: GOLD,
          fontFamily: SANS,
          fontSize: 11,
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {eyebrow}
      </div>
      <h2
        style={{
          fontFamily: SERIF,
          color: "#F5F1E3",
          fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)",
          fontWeight: 700,
          margin: "12px 0 10px",
          lineHeight: 1.15,
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          style={{
            color: MUTED,
            fontFamily: SANS,
            fontSize: "1rem",
            maxWidth: 620,
            margin: "0 auto",
            lineHeight: 1.55,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ---- Council Induction · Grand Opening ---------------------
// Cinematic 22.3-second intro. Two states:
//   fresh       → full-width autoplaying (muted) hero with big
//                 gold "TAP TO ENABLE SOUND" overlay
//   collapsed   → compact "REPLAY GRAND OPENING" button once the
//                 visitor has watched or dismissed the induction
// Sessioned via `wa_induction_watched` so the pill state
// survives navigation without hard-refreshing on every click.
function CouncilInduction({ paused = false }) {
  const [collapsed, setCollapsed] = useState(() => hasWatchedInduction());
  const [muted, setMuted] = useState(true);
  const [showPoster, setShowPoster] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (collapsed) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => { /* autoplay denied */ });
  }, [collapsed]);

  // Race guard — when a Speaker lightbox opens (paused === true),
  // pause the induction so two <video> elements aren't playing at
  // once. When the lightbox closes, resume playback only if the
  // induction was still mid-clip and hasn't been dismissed.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || collapsed) return;
    if (paused) {
      try { v.pause(); } catch (_) { /* ignore */ }
    } else if (v.paused && !v.ended) {
      v.play().catch(() => { /* autoplay denied */ });
    }
  }, [paused, collapsed]);

  const enableSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    setShowPoster(false);
    v.currentTime = 0;
    v.play().catch(() => { /* ignore */ });
  };

  const dismissAndCollapse = () => {
    markInductionWatched();
    setCollapsed(true);
  };

  const replay = () => {
    setCollapsed(false);
    setMuted(true);
    setShowPoster(true);
  };

  if (collapsed) {
    return (
      <div
        data-testid="council-induction-collapsed"
        style={{ padding: "26px 24px 0", textAlign: "center" }}
      >
        <button
          type="button"
          onClick={replay}
          data-testid="council-induction-replay"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 18px",
            background: "transparent",
            color: GOLD,
            border: `1px solid ${GOLD_SOFT}`,
            borderRadius: 999,
            fontFamily: SANS,
            fontSize: 11,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>▶</span>
          Replay Grand Opening
        </button>
      </div>
    );
  }

  return (
    <section
      data-testid="council-induction"
      style={{ padding: "24px 24px 8px", maxWidth: 1240, margin: "0 auto" }}
    >
      <div
        style={{
          color: GOLD,
          fontFamily: SANS,
          fontSize: 11,
          letterSpacing: "0.42em",
          textTransform: "uppercase",
          fontWeight: 700,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        Grand Opening · Council Induction
      </div>
      <div
        style={{
          position: "relative",
          borderRadius: 16,
          overflow: "hidden",
          border: `1px solid ${GOLD}`,
          background: "#000",
          boxShadow: `0 40px 100px -32px rgba(201,168,76,0.45)`,
          aspectRatio: "16 / 9",
        }}
      >
        <video
          ref={videoRef}
          src={COUNCIL_INDUCTION_VIDEO}
          data-testid="council-induction-video"
          controls={!showPoster}
          autoPlay
          muted
          playsInline
          onEnded={dismissAndCollapse}
          style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
        />
        {showPoster && (
          <button
            type="button"
            onClick={enableSound}
            data-testid="council-induction-unmute"
            aria-label="Enable sound and play grand opening"
            style={{
              position: "absolute",
              inset: 0,
              background: "radial-gradient(60% 60% at 50% 55%, rgba(0,0,0,0.35), rgba(0,0,0,0.75) 90%)",
              border: "none",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 20,
              padding: 24,
              color: "#F5F1E3",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 92, height: 92, borderRadius: "50%",
                background: `linear-gradient(160deg, ${GOLD} 0%, #B4923D 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 20px 50px -12px rgba(201,168,76,0.7)`,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 0, height: 0,
                  borderLeft: "26px solid #0A0A0A",
                  borderTop: "16px solid transparent",
                  borderBottom: "16px solid transparent",
                  marginLeft: 8,
                }}
              />
            </span>
            <div
              style={{
                fontFamily: SANS,
                fontSize: 12,
                letterSpacing: "0.4em",
                textTransform: "uppercase",
                fontWeight: 800,
                color: GOLD,
              }}
            >
              Tap To Enable Sound
            </div>
            <div
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: "1.05rem",
                color: "#F5F1E3",
                maxWidth: 520,
                textAlign: "center",
                lineHeight: 1.45,
              }}
            >
              Amanda is delivering the Council Induction. Tap once to hear the briefing in full.
            </div>
          </button>
        )}
        <button
          type="button"
          onClick={dismissAndCollapse}
          data-testid="council-induction-skip"
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            padding: "8px 14px",
            background: "rgba(10,10,10,0.7)",
            color: GOLD,
            border: `1px solid ${GOLD_SOFT}`,
            borderRadius: 999,
            fontFamily: SANS,
            fontSize: 10,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            fontWeight: 700,
            cursor: "pointer",
            zIndex: 3,
          }}
        >
          Skip Intro →
        </button>
      </div>
      {muted && !showPoster && (
        <div
          style={{
            textAlign: "center",
            marginTop: 10,
            color: MUTED,
            fontFamily: SANS,
            fontSize: 12,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Playing muted · use player controls to enable sound
        </div>
      )}
    </section>
  );
}

// ---- Video lightbox ----------------------------------------
// UX hardening (2026-02): make it impossible to get "stuck" in
// the modal. Escape hatches: (1) big X + "CLOSE" label in the
// corner, (2) full-width "← Back to Stage" secondary link under
// the video, (3) click anywhere on the backdrop, (4) Esc key.
// The video is paused + rewound before unmount so audio never
// bleeds into the stage view. z-index 9999 keeps the close chrome
// above any player controls or picture-in-picture overlays.
function VideoLightbox({ member, onClose }) {
  const videoRef = useRef(null);

  // Belt-and-braces: on ANY unmount path (Esc, backdrop, X,
  // route change) hard-stop the audio track before the element
  // is torn down. Some browsers keep the audio buffer alive
  // briefly if the element unmounts mid-playback.
  useEffect(() => {
    return () => {
      const v = videoRef.current;
      if (v) {
        try {
          v.pause();
          v.currentTime = 0;
          v.removeAttribute("src");
          v.load();
        } catch (_) { /* ignore */ }
      }
    };
  }, []);

  const handleClose = (e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
        v.currentTime = 0;
      } catch (_) { /* ignore */ }
    }
    onClose();
  };

  if (!member) return null;

  return (
    <div
      data-testid="roundtable-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${member.key} — ${member.title}`}
      onClick={handleClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.95)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 24px 24px",
        animation: "rt-fade-in 260ms ease both",
        cursor: "zoom-out",
      }}
    >
      {/* Large, always-visible close chrome — sits on the
          backdrop (NOT the content wrapper) so it can never be
          covered by native video controls or the video element. */}
      <button
        type="button"
        onClick={handleClose}
        aria-label="Close video and return to stage"
        data-testid="roundtable-lightbox-close"
        style={{
          position: "fixed",
          top: 20,
          right: 20,
          height: 52,
          minWidth: 132,
          padding: "0 20px 0 16px",
          borderRadius: 999,
          background: GOLD,
          border: "none",
          color: "#0A0A0A",
          fontFamily: SANS,
          fontSize: 13,
          fontWeight: 800,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
          zIndex: 10001,
          boxShadow: `0 18px 40px -14px rgba(0,0,0,0.9), 0 0 0 2px rgba(10,10,10,0.6)`,
        }}
      >
        <span aria-hidden style={{ fontSize: 26, lineHeight: 1, fontWeight: 900 }}>×</span>
        <span>Close</span>
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: 1080,
          width: "100%",
          background: "#000",
          border: `1px solid ${GOLD}`,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: `0 40px 100px -30px rgba(201,168,76,0.55)`,
          cursor: "auto",
        }}
      >
        <video
          ref={videoRef}
          src={member.video}
          controls
          autoPlay
          playsInline
          data-testid="roundtable-lightbox-video"
          style={{
            width: "100%",
            display: "block",
            background: "#000",
            aspectRatio: "16 / 9",
          }}
        />
        <div
          style={{
            padding: "16px 22px 18px",
            borderTop: `1px solid ${HAIRLINE}`,
            background: "#0A0A0A",
          }}
        >
          <div
            style={{
              fontFamily: SERIF,
              color: GOLD,
              fontSize: "1.6rem",
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {member.key.charAt(0) + member.key.slice(1).toLowerCase()}
          </div>
          <div
            style={{
              marginTop: 6,
              color: "#E8E4D6",
              fontFamily: SANS,
              fontSize: "0.98rem",
              fontWeight: 600,
            }}
          >
            {member.title}
          </div>
          <div
            style={{
              marginTop: 8,
              color: GOLD,
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: "0.95rem",
            }}
          >
            {member.strengths}
          </div>
        </div>

        {/* Secondary escape route — a highly-visible textual
            link right under the video, always reachable even
            on tiny mobile viewports where the top X might feel
            far from the thumb. */}
        <button
          type="button"
          onClick={handleClose}
          data-testid="roundtable-lightbox-back"
          style={{
            width: "100%",
            padding: "14px 18px",
            background: "#0A0A0A",
            color: GOLD,
            border: "none",
            borderTop: `1px solid ${HAIRLINE}`,
            fontFamily: SANS,
            fontSize: "0.92rem",
            fontWeight: 700,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          ← Back to Stage
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------
// SANDBOX — See Your Shop on the Grid
// ---------------------------------------------------------------
// The `SeeYourShopButton` fades in inside the answer card once
// the council has delivered a response. Clicking it opens the
// fullscreen `SandboxBackoffice` overlay which pretends the
// prospect's shop is already wired into the Grid.
// ---------------------------------------------------------------
function SeeYourShopButton({ onClick }) {
  return (
    <div
      className="rt-fade"
      style={{ marginTop: 22, textAlign: "center" }}
    >
      <button
        type="button"
        onClick={onClick}
        data-testid="see-your-shop-btn"
        style={{
          display: "inline-block",
          background: `linear-gradient(135deg, ${GOLD} 0%, #E4C065 100%)`,
          color: "#0A0A0A",
          padding: "16px 30px",
          fontFamily: SANS,
          fontSize: "0.92rem",
          border: "none",
          borderRadius: 999,
          cursor: "pointer",
          fontWeight: 800,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          boxShadow: `0 22px 44px -14px ${GOLD}, 0 0 0 1px rgba(255,255,255,0.08) inset`,
          animation: "rt-glow 2.4s ease-in-out infinite",
        }}
      >
        See Your Shop On The Grid →
      </button>
    </div>
  );
}

function SandboxBackoffice({ company, city, onClose }) {
  return (
    <div
      data-testid="sandbox-backoffice"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(4,4,4,0.88)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflowY: "auto",
        padding: "40px 20px",
        fontFamily: SANS,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 960,
          background: "linear-gradient(180deg, #0F0F0F 0%, #050505 100%)",
          border: `1px solid ${GOLD_SOFT}`,
          borderRadius: 18,
          padding: "36px 32px 40px",
          color: "#F1EEDF",
          boxShadow: `0 60px 120px -40px ${GOLD_SOFT}, 0 0 0 1px rgba(255,255,255,0.03) inset`,
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="sandbox-close"
          aria-label="Close sandbox"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "transparent",
            border: `1px solid ${HAIRLINE}`,
            color: GOLD,
            fontSize: 18,
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ✕
        </button>

        <div
          style={{
            display: "inline-block",
            padding: "6px 14px",
            border: `1px solid ${GOLD_SOFT}`,
            color: GOLD,
            fontSize: 10,
            letterSpacing: "0.34em",
            textTransform: "uppercase",
            fontWeight: 700,
            borderRadius: 999,
            marginBottom: 14,
          }}
        >
          Sandbox · Backoffice Preview
        </div>
        <h2
          data-testid="sandbox-title"
          style={{
            fontFamily: SERIF,
            fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
            fontWeight: 700,
            color: "#F5F1E3",
            margin: 0,
            letterSpacing: "-0.005em",
          }}
        >
          {company} on the Grid
        </h2>
        <div
          style={{
            marginTop: 8,
            color: MUTED,
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: "1rem",
          }}
        >
          A live-feel preview of your Command Center — every KPI
          is a placeholder until you activate a node.
        </div>

        {/* --- KPI Cards --- */}
        <div
          data-testid="sandbox-kpis"
          style={{
            marginTop: 26,
            display: "grid",
            gap: 14,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          <KpiCard
            testId="sandbox-kpi-shield"
            label="Intake Shield"
            value="ACTIVE"
            accent="#4ADE80"
            sublabel={`${company} · Amanda 4.0`}
            icon="🛡"
          />
          <KpiCard
            testId="sandbox-kpi-calls"
            label="Missed Calls Stopped"
            value="14"
            accent={GOLD}
            sublabel="Last 30 days ↑ 42%"
            icon="↑"
          />
          <KpiCard
            testId="sandbox-kpi-rank"
            label="Market Rank"
            value={`${city} · Top 5%`}
            accent="#F5F1E3"
            sublabel="Grid-verified visibility"
            icon="★"
          />
        </div>

        {/* --- Action Key Tiles --- */}
        <SandboxSection title="Action Keys" eyebrow="Available Tools">
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            }}
          >
            <ActionKeyTile
              testId="action-key-dial"
              icon="📞"
              title="Dial-Now"
              blurb="Instant callback routing"
              badge="Activate"
            />
            <ActionKeyTile
              testId="action-key-invoicing"
              icon="🧾"
              title="Invoicing"
              blurb="One-tap invoice dispatch"
              badge="Activate"
            />
            <ActionKeyTile
              testId="action-key-content"
              icon="📣"
              title="Content"
              blurb="AI content engine"
              badge="Coming Soon"
            />
          </div>
        </SandboxSection>

        {/* --- Amber Audit + Lead Bleed Report --- */}
        <SandboxSection
          title="Grid Audit"
          eyebrow="Amber · Quality Assurance"
        >
          <AmberAudit />
        </SandboxSection>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, sublabel, icon, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 14,
        padding: "18px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            color: MUTED,
            fontSize: 10,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          {label}
        </span>
        <span aria-hidden style={{ fontSize: 18, color: accent }}>{icon}</span>
      </div>
      <div
        style={{
          fontFamily: SERIF,
          fontSize: "1.7rem",
          fontWeight: 700,
          color: accent,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div style={{ color: MUTED, fontSize: 12, letterSpacing: "0.04em" }}>
        {sublabel}
      </div>
    </div>
  );
}

function ActionKeyTile({ icon, title, blurb, badge, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 12,
        padding: "16px 16px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        cursor: "pointer",
        transition: "transform 220ms ease, border-color 220ms ease, background 220ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = GOLD;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = HAIRLINE;
      }}
    >
      <div
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: "rgba(201,168,76,0.10)",
          border: `1px solid ${GOLD_SOFT}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: "#F5F1E3", fontWeight: 700, fontSize: "1rem" }}>
          {title}
        </div>
        <div style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>
          {blurb}
        </div>
      </div>
      <span
        style={{
          fontSize: 10,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 800,
          color: badge === "Activate" ? "#0A0A0A" : GOLD,
          background: badge === "Activate" ? GOLD : "rgba(201,168,76,0.10)",
          border: `1px solid ${GOLD_SOFT}`,
          padding: "5px 10px",
          borderRadius: 999,
          whiteSpace: "nowrap",
        }}
      >
        {badge}
      </span>
    </div>
  );
}

function SandboxSection({ title, eyebrow, children }) {
  return (
    <div style={{ marginTop: 32 }}>
      <div
        style={{
          color: GOLD,
          fontSize: 10,
          letterSpacing: "0.34em",
          textTransform: "uppercase",
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        {eyebrow}
      </div>
      <h3
        style={{
          margin: "0 0 16px",
          fontFamily: SERIF,
          fontSize: "1.35rem",
          fontWeight: 700,
          color: "#F5F1E3",
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------
// AMBER · Grid Audit
// ---------------------------------------------------------------
// 3-question flow → Lead Bleed Report. All logic is client-side
// so the modal feels instant. The final CTA lands on /activate-node.
// ---------------------------------------------------------------
const AMBER_QUESTIONS = [
  {
    key: "missed",
    prompt: "How many calls does your business miss per week?",
    options: [
      { value: "low", label: "0 – 5" },
      { value: "mid", label: "5 – 15" },
      { value: "high", label: "15+" },
    ],
  },
  {
    key: "followup",
    prompt: "What's your current follow-up time after a missed call?",
    options: [
      { value: "under5", label: "Under 5 min" },
      { value: "under30", label: "5 – 30 min" },
      { value: "over30", label: "Over 30 min" },
      { value: "none", label: "No system" },
    ],
  },
  {
    key: "auto",
    prompt: "Are you currently running any automated outreach?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "unsure", label: "Not sure" },
    ],
  },
];

function computeBleedReport(answers) {
  // Rough deterministic model — pessimistic on high answers so the
  // Report reads urgent, but never hallucinates $0.
  const missedPerWeek = { low: 3, mid: 10, high: 20 }[answers.missed] || 3;
  const AVG_TICKET = 3200; // matches Sterling's Surgical Strike script
  const monthlyLost = missedPerWeek * 4 * AVG_TICKET;

  const followupRisk =
    { under5: 0, under30: 1, over30: 2, none: 3 }[answers.followup] ?? 3;
  const missedRisk = { low: 0, mid: 1, high: 2 }[answers.missed] || 0;
  const autoRisk = { yes: 0, unsure: 1, no: 2 }[answers.auto] ?? 2;
  const total = followupRisk + missedRisk + autoRisk;

  let level = "Low";
  if (total >= 5) level = "High";
  else if (total >= 3) level = "Medium";

  // Recovery scales with the pain — a High-risk shop has more to
  // recover than a Low-risk one already running follow-up.
  const recovery = level === "High" ? 78 : level === "Medium" ? 62 : 41;
  return { monthlyLost, level, recovery };
}

function AmberAudit() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= AMBER_QUESTIONS.length;
  const report = useMemo(
    () => (done ? computeBleedReport(answers) : null),
    [done, answers],
  );

  const pick = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setStep((s) => s + 1);
  };
  const reset = () => {
    setAnswers({});
    setStep(0);
  };

  if (done && report) {
    return <LeadBleedReport report={report} onReset={reset} />;
  }

  const q = AMBER_QUESTIONS[step];
  return (
    <div
      data-testid={`amber-question-${step}`}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 12,
        padding: "20px 20px",
      }}
    >
      <div
        style={{
          color: MUTED,
          fontSize: 10,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        Amber · Question {step + 1} of {AMBER_QUESTIONS.length}
      </div>
      <div
        style={{
          color: "#F5F1E3",
          fontSize: "1.1rem",
          fontFamily: SERIF,
          lineHeight: 1.45,
          marginBottom: 16,
        }}
      >
        {q.prompt}
      </div>
      <div
        style={{
          display: "grid",
          gap: 10,
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        }}
      >
        {q.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            data-testid={`amber-opt-${q.key}-${opt.value}`}
            onClick={() => pick(q.key, opt.value)}
            style={{
              background: "transparent",
              color: "#F5F1E3",
              border: `1px solid ${GOLD_SOFT}`,
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: "0.95rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              cursor: "pointer",
              transition: "all 200ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = GOLD;
              e.currentTarget.style.color = "#0A0A0A";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#F5F1E3";
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function LeadBleedReport({ report, onReset }) {
  const levelColor =
    report.level === "High" ? "#EF4444"
    : report.level === "Medium" ? "#F59E0B"
    : "#4ADE80";
  const fmt = (n) =>
    "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return (
    <div
      data-testid="lead-bleed-report"
      style={{
        background: "linear-gradient(160deg, rgba(239,68,68,0.10) 0%, rgba(10,10,10,0.9) 60%)",
        border: `1px solid ${levelColor}55`,
        borderRadius: 14,
        padding: "22px 22px 20px",
      }}
    >
      <div
        style={{
          color: levelColor,
          fontSize: 10,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        🩸 Lead Bleed Report
      </div>
      <div
        style={{
          fontFamily: SERIF,
          color: "#F5F1E3",
          fontSize: "1.6rem",
          fontWeight: 700,
          lineHeight: 1.15,
        }}
      >
        Estimated Monthly Revenue Lost:{" "}
        <span data-testid="lead-bleed-amount" style={{ color: levelColor }}>{fmt(report.monthlyLost)}</span>
      </div>

      <div
        style={{
          marginTop: 16,
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 10,
          }}
        >
          <div style={{ color: MUTED, fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 700 }}>
            Risk Level
          </div>
          <div
            data-testid="lead-bleed-risk"
            style={{
              marginTop: 4,
              fontFamily: SERIF,
              fontSize: "1.25rem",
              fontWeight: 700,
              color: levelColor,
            }}
          >
            {report.level}
          </div>
        </div>
        <div
          style={{
            padding: "12px 14px",
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 10,
          }}
        >
          <div style={{ color: MUTED, fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 700 }}>
            Recovery Potential
          </div>
          <div
            data-testid="lead-bleed-recovery"
            style={{
              marginTop: 4,
              fontFamily: SERIF,
              fontSize: "1.25rem",
              fontWeight: 700,
              color: GOLD,
            }}
          >
            {report.recovery}%
          </div>
        </div>
      </div>

      <Link
        to="/activate-node"
        data-testid="lead-bleed-cta"
        style={{
          display: "block",
          marginTop: 22,
          textAlign: "center",
          background: `linear-gradient(135deg, ${GOLD} 0%, #E4C065 100%)`,
          color: "#0A0A0A",
          padding: "16px 24px",
          fontFamily: SANS,
          fontSize: "0.94rem",
          borderRadius: 12,
          textDecoration: "none",
          fontWeight: 800,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          boxShadow: `0 22px 44px -14px ${GOLD}`,
          animation: "rt-glow 2s ease-in-out infinite",
        }}
      >
        Stop The Bleed → Activate Your Node
      </Link>

      <button
        type="button"
        onClick={onReset}
        data-testid="lead-bleed-reset"
        style={{
          marginTop: 12,
          width: "100%",
          background: "transparent",
          color: MUTED,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 8,
          padding: "10px",
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        Re-run Audit
      </button>
    </div>
  );
}

// ---------------------------------------------------------------
// SURGICAL STRIKE SCRIPTS
// ---------------------------------------------------------------
// 4 x 15-second commercial scripts. Each ships with a deep-link
// URL so ops can drop it into their ad copy. The URL uses the
// current origin so scripts stay accurate across preview + prod.
// ---------------------------------------------------------------
const SURGICAL_SCRIPTS = [
  {
    key: "amanda",
    expert: "AMANDA",
    title: "Amanda · Trust & Onboarding",
    q: "How do I stop missing leads",
    tag: "warm, direct to camera",
    body:
      `"Your next client just called... and no one picked up.\n` +
      `That's not a missed call — that's a missed mortgage.\n` +
      `I built your shield in 90 seconds. See it live."`,
  },
  {
    key: "sterling",
    expert: "STERLING",
    title: "Sterling · Operations & Revenue",
    q: "Show me my revenue leak",
    tag: "confident, boardroom energy",
    body:
      `"Fourteen missed calls last month. At $3,200 average ticket...\n` +
      `that's forty-four thousand eight hundred dollars — gone.\n` +
      `Your Grid Audit takes 30 seconds."`,
  },
  {
    key: "sloan",
    expert: "SLOAN",
    title: "Sloan · Strategy & Market Position",
    q: "What is my market position",
    tag: "analytical, calm authority",
    body:
      `"Your competitor answered their phone. You didn't.\n` +
      `They're now top 5% in your city. You're not.\n` +
      `One question changes that. Ask it."`,
  },
  {
    key: "vance",
    expert: "VANCE",
    title: "Vance · Technology & Systems",
    q: "Show me my lost leads",
    tag: "tech-forward, energetic",
    body:
      `"I can show you every lead that slipped through your system\n` +
      `in the last 30 days. Not a report — a live reconstruction.\n` +
      `Your sandbox is waiting."`,
  },
];

function surgicalStrikeUrl(expert, q) {
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";
  return `${origin}/roundtable?expert=${encodeURIComponent(expert)}&q=${encodeURIComponent(q)}`;
}

function SurgicalScripts() {
  const [openKey, setOpenKey] = useState(null);
  return (
    <section
      data-testid="surgical-strike-scripts"
      style={{
        padding: "56px 24px 24px",
        maxWidth: 940,
        margin: "0 auto",
      }}
    >
      <SectionHeading
        eyebrow="Ad Library"
        title="Surgical Strike Scripts"
        subtitle="Four 15-second commercial scripts, each with a deep-link into the Council. Copy the CTA URL and drop it into your next campaign."
      />

      <div style={{ marginTop: 24, display: "grid", gap: 14 }}>
        {SURGICAL_SCRIPTS.map((s) => {
          const open = openKey === s.key;
          const url = surgicalStrikeUrl(s.expert, s.q);
          return (
            <ScriptCard
              key={s.key}
              script={s}
              url={url}
              open={open}
              onToggle={() =>
                setOpenKey((cur) => (cur === s.key ? null : s.key))
              }
            />
          );
        })}
      </div>
    </section>
  );
}

function ScriptCard({ script, url, open, onToggle }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* silent — the URL is visible in the reveal */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return (
    <article
      data-testid={`script-card-${script.key}`}
      style={{
        background: CARD,
        border: `1px solid ${open ? GOLD : HAIRLINE}`,
        borderRadius: 12,
        overflow: "hidden",
        transition: "border-color 240ms ease",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`script-toggle-${script.key}`}
        aria-expanded={open}
        style={{
          all: "unset",
          boxSizing: "border-box",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "16px 18px",
          minHeight: 60,
          cursor: "pointer",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: `1px solid ${GOLD_SOFT}`,
            background: "rgba(201,168,76,0.08)",
            color: GOLD,
            fontFamily: SERIF,
            fontSize: 18,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {script.expert.charAt(0)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: SERIF,
              color: "#F5F1E3",
              fontSize: "1.05rem",
              fontWeight: 700,
              letterSpacing: "-0.005em",
            }}
          >
            {script.title}
          </div>
          <div
            style={{
              color: MUTED,
              fontSize: 12,
              marginTop: 2,
              letterSpacing: "0.02em",
              fontStyle: "italic",
            }}
          >
            {script.tag}
          </div>
        </div>
        <span
          aria-hidden
          style={{
            color: open ? GOLD : MUTED,
            fontSize: 14,
            lineHeight: 1,
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 240ms ease, color 240ms ease",
            flexShrink: 0,
          }}
        >
          ▸
        </span>
      </button>

      <div
        data-testid={`script-body-${script.key}`}
        style={{
          maxHeight: open ? 640 : 0,
          overflow: "hidden",
          transition: "max-height 360ms ease, padding 260ms ease",
          background: CARD_HI,
        }}
      >
        <div style={{ padding: open ? "16px 18px 20px" : "0 18px" }}>
          <div
            style={{
              padding: "14px 16px",
              background: INK,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 8,
              color: "#F0EDE0",
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: "0.98rem",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {script.body}
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              background: "rgba(201,168,76,0.06)",
              border: `1px solid ${GOLD_SOFT}`,
              borderRadius: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                color: GOLD,
                fontSize: 10,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                fontWeight: 800,
                minWidth: 40,
              }}
            >
              CTA
            </span>
            <span
              data-testid={`script-url-${script.key}`}
              style={{
                flex: 1,
                minWidth: 200,
                color: "#F5F1E3",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.82rem",
                wordBreak: "break-all",
              }}
            >
              {url}
            </span>
            <button
              type="button"
              onClick={copy}
              data-testid={`script-copy-${script.key}`}
              style={{
                background: copied ? "#4ADE80" : "transparent",
                color: copied ? "#0A2411" : GOLD,
                border: `1px solid ${copied ? "#4ADE80" : GOLD_SOFT}`,
                padding: "6px 12px",
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 800,
                borderRadius: 6,
                cursor: "pointer",
                transition: "all 200ms ease",
              }}
            >
              {copied ? "Copied" : "Copy Link"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
